import React from 'react';
import ReactECharts from 'echarts-for-react';

const PropertyPerformance = ({ properties, transactions }) => {
  // 1. Calculate Metrics per Property
  const performanceData = properties.map(prop => {
    const propTx = transactions.filter(t => t.propertyId === prop.id);
    const income = propTx.filter(t => t.type === 'Income').reduce((sum, t) => sum + Number(t.amount), 0);
    const expenses = propTx.filter(t => t.type === 'Expense').reduce((sum, t) => sum + Number(t.amount), 0);
    const profit = income - expenses;
    const margin = income > 0 ? (profit / income) * 100 : 0;

    return {
      name: prop.name,
      income,
      expenses,
      profit,
      margin: parseFloat(margin.toFixed(1))
    };
  }).sort((a, b) => b.profit - a.profit);

  // 2. Chart Options - Profitability Leaderboard
  const profitOption = {
    tooltip: { trigger: 'axis', axisPointer: { type: 'shadow' } },
    grid: { left: '3%', right: '4%', bottom: '3%', top: '5%', containLabel: true },
    xAxis: { type: 'value', axisLabel: { formatter: '${value}' } },
    yAxis: { type: 'category', data: performanceData.map(d => d.name).reverse() },
    series: [
      {
        name: 'Net Profit',
        type: 'bar',
        data: performanceData.map(d => d.profit).reverse(),
        itemStyle: {
          color: (params) => params.value >= 0 ? '#4F46E5' : '#EF4444',
          borderRadius: [0, 4, 4, 0]
        }
      }
    ]
  };

  // 3. Chart Options - Income vs Expense Ratio
  const ratioOption = {
    tooltip: { trigger: 'axis', axisPointer: { type: 'shadow' } },
    legend: { bottom: 0 },
    grid: { left: '3%', right: '4%', bottom: '15%', top: '5%', containLabel: true },
    xAxis: { type: 'category', data: performanceData.map(d => d.name) },
    yAxis: { type: 'value', axisLabel: { formatter: '${value}' } },
    series: [
      {
        name: 'Income',
        type: 'bar',
        stack: 'total',
        data: performanceData.map(d => d.income),
        itemStyle: { color: '#10B981' }
      },
      {
        name: 'Expenses',
        type: 'bar',
        stack: 'total',
        data: performanceData.map(d => d.expenses),
        itemStyle: { color: '#EF4444' }
      }
    ]
  };

  return (
    <div className="space-y-6 animate-in fade-in duration-500">
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Profitability Leaderboard */}
        <div className="bg-white p-6 rounded-xl border border-gray-100 shadow-sm">
          <div className="mb-4">
            <h4 className="text-sm font-bold text-gray-700 uppercase tracking-wider">Profitability Leaderboard</h4>
            <p className="text-xs text-gray-500">Net profit comparison across properties</p>
          </div>
          <div className="h-[350px]">
            <ReactECharts option={profitOption} style={{ height: '100%' }} />
          </div>
        </div>

        {/* Income vs Expense Breakdown */}
        <div className="bg-white p-6 rounded-xl border border-gray-100 shadow-sm">
          <div className="mb-4">
            <h4 className="text-sm font-bold text-gray-700 uppercase tracking-wider">Revenue vs. Burn</h4>
            <p className="text-xs text-gray-500">Stacked income and expense totals</p>
          </div>
          <div className="h-[350px]">
            <ReactECharts option={ratioOption} style={{ height: '100%' }} />
          </div>
        </div>
      </div>

      {/* Numerical Performance Table */}
      <div className="bg-white rounded-xl border border-gray-100 shadow-sm overflow-hidden">
        <div className="px-6 py-4 border-b border-gray-100 bg-gray-50">
          <h4 className="text-sm font-bold text-gray-700 uppercase tracking-wider">Efficiency Metrics</h4>
        </div>
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50/50">
              <tr>
                <th className="px-6 py-3 text-left text-[10px] font-bold text-gray-500 uppercase">Property</th>
                <th className="px-6 py-3 text-right text-[10px] font-bold text-gray-500 uppercase">Total Income</th>
                <th className="px-6 py-3 text-right text-[10px] font-bold text-gray-500 uppercase">Total Expense</th>
                <th className="px-6 py-3 text-right text-[10px] font-bold text-gray-500 uppercase">Net Profit</th>
                <th className="px-6 py-3 text-right text-[10px] font-bold text-gray-500 uppercase">Margin (%)</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {performanceData.map((item, idx) => (
                <tr key={idx} className="hover:bg-gray-50 transition-colors">
                  <td className="px-6 py-4 text-sm font-bold text-gray-900">{item.name}</td>
                  <td className="px-6 py-4 text-sm text-right text-green-600 font-medium">${item.income.toLocaleString()}</td>
                  <td className="px-6 py-4 text-sm text-right text-red-500 font-medium">${item.expenses.toLocaleString()}</td>
                  <td className={`px-6 py-4 text-sm text-right font-bold ${item.profit >= 0 ? 'text-gray-900' : 'text-red-700'}`}>
                    ${item.profit.toLocaleString()}
                  </td>
                  <td className="px-6 py-4 text-right">
                    <span className={`px-2 py-1 rounded text-xs font-bold ${item.margin > 50 ? 'bg-green-100 text-green-700' : item.margin > 20 ? 'bg-blue-100 text-blue-700' : 'bg-orange-100 text-orange-700'}`}>
                      {item.margin}%
                    </span>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
};

export default PropertyPerformance;